import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MButton } from "@/components/ui/atoms/MButtons";
import { IconActionCard } from "@/components/icons";
import { useOverdueReminders } from "@/hooks/useOverdueReminders";
import styles from "@/pages/Dashboard/components/ActionCard.module.css";

const OverdueRemindersCard: React.FC<{ className?: string }> = ({
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    overdueCount,
    overdueItems,
    overdueReminderIds,
    notificationHealth,
    openNotificationSettings,
    clearReminder,
  } = useOverdueReminders();

  const handleView = () => {
    // Tell native to dismiss these notifications from the tray so they don't reappear on app resume
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "dismiss_overdue_reminders",
          payload: { reminderIds: overdueReminderIds },
        })
      );
    }
    overdueReminderIds.forEach((id) => clearReminder(id));
    navigate("/reminders");
  };

  if (notificationHealth?.criticalAlertsDisabled) {
    return (
      <div className={`${styles.actionCardContainer} w-full ${className}`}>
        <div className={styles.actionCardTitle}>
          {t("reminders.health.criticalAlertsDisabledTitle")}
        </div>
        <div className={styles.actionCardDescription}>
          {t("reminders.health.criticalAlertsDisabledBody")}
        </div>
        <div className={styles.actionCardCTA}>
          <MButton
            variant="filled"
            size="sm"
            onClick={openNotificationSettings}
            className={styles.acceptButton}
          >
            {t("common.allow")}
          </MButton>
        </div>
        <div className={styles.newSvgIcon}>
          <IconActionCard size={162} color="#F6E7C4" />
        </div>
      </div>
    );
  }

  if (overdueCount === 0) return null;

  const item = overdueItems[0] ?? {
    reminderId: "",
    title: t("reminders.overdue.title", { count: overdueCount }),
    body: t("reminders.overdue.body"),
    reminderType: "MEDICATION",
  };

  const title =
    overdueCount > 1 ? `${item.title} (+${overdueCount - 1} more)` : item.title;

  return (
    <div className={`${styles.actionCardContainer} w-full ${className}`}>
      <div className={styles.actionCardTitle}>{title}</div>
      <div className={styles.actionCardDescription}>{item.body}</div>
      <div className={styles.actionCardCTA}>
        <MButton
          variant="filled"
          size="sm"
          onClick={handleView}
          className={styles.acceptButton}
        >
          {t("reminders.overdue.viewReminders")}
        </MButton>
      </div>
      <div className={styles.newSvgIcon}>
        <IconActionCard size={162} color="#F6E7C4" />
      </div>
    </div>
  );
};

export default OverdueRemindersCard;
